import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { Robot, ArrowRight, GoogleLogo, EnvelopeSimple, Lock, User } from "@phosphor-icons/react";
import Field from "@/components/Field";

export default function Login({ mode = "login" }) {
  const isRegister = mode === "register";
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (isRegister) await register(email.trim().toLowerCase(), password, name.trim());
      else await login(email.trim().toLowerCase(), password);
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex bg-[var(--ink)] text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-[0.04]" />
        <Link to="/" className="flex items-center gap-2 relative z-10" data-testid="brand-link">
          <div className="w-9 h-9 bg-[var(--accent)] flex items-center justify-center">
            <Robot size={20} weight="duotone" color="#0A0A0A" />
          </div>
          <span className="font-display text-2xl">Robocope</span>
        </Link>
        <div className="relative z-10">
          <div className="overline text-white/50 mb-4">/ Day one</div>
          <h2 className="font-display text-5xl xl:text-6xl tracking-tighter leading-[0.95]">
            Onboard your <span className="bg-[var(--accent)] text-[var(--ink)] px-2 -rotate-1 inline-block">AI employee</span> in under 60 seconds.
          </h2>
          <p className="text-white/70 mt-6 max-w-md">
            Email triage, lead scoring, customer chat — all running by tomorrow morning's coffee.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-xs text-white/40 font-mono-rc">
          <span className="w-2 h-2 bg-[var(--accent)] rounded-full" />
          ROBOCOPE.COM · V1
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="overline text-[var(--ink-soft)]">/ {isRegister ? "Create your workspace" : "Welcome back"}</div>
          <h1 className="font-display text-4xl tracking-tight mt-2 mb-2">
            {isRegister ? "Hire your AI employee." : "Log back in."}
          </h1>
          <p className="text-[var(--ink-soft)] mb-8">
            {isRegister ? "Free 14-day trial. No card. Real automations." : "Pick up exactly where you left off."}
          </p>

          <button
            type="button"
            onClick={onGoogle}
            data-testid="google-login-btn"
            className="w-full btn-secondary justify-center mb-4"
          >
            <GoogleLogo size={20} weight="bold" /> Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[var(--line)]" />
            <span className="overline text-[var(--ink-soft)]">or</span>
            <div className="flex-1 h-px bg-[var(--line)]" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="auth-form">
            {isRegister && (
              <Field label="Full name" icon={User}>
                <input
                  required type="text" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Jasmine Park" className="input-rc pl-10"
                  data-testid="name-input"
                />
              </Field>
            )}
            <Field label="Email" icon={EnvelopeSimple}>
              <input
                required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" className="input-rc pl-10"
                data-testid="email-input"
              />
            </Field>
            <Field label="Password" icon={Lock}>
              <input
                required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="input-rc pl-10"
                data-testid="password-input"
              />
            </Field>

            {error && <div className="cell p-3 text-sm bg-red-50 text-red-700 border-red-200" data-testid="auth-error">{error}</div>}

            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-btn"
              className="btn-primary w-full justify-center"
            >
              {busy ? "Working…" : isRegister ? "Create workspace" : "Log in"}
              <ArrowRight size={18} weight="bold" />
            </button>
          </form>

          <div className="mt-6 text-sm text-[var(--ink-soft)]">
            {isRegister ? (
              <>Already have an account? <Link to="/login" className="font-medium underline" data-testid="switch-to-login">Log in</Link></>
            ) : (
              <>
                <Link to="/forgot-password" className="font-medium underline mr-4" data-testid="forgot-password-link">Forgot password?</Link>
                New here? <Link to="/register" className="font-medium underline" data-testid="switch-to-register">Create a workspace</Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
